import { chooseFolderOnce } from "./projectFolder";
// ROUM-SCANPDF-INDIV-20260621 — 스캔본 생성 공용 유틸 (PdfEditor.applyScanEffect 재사용)
// 입력 PDF(날인본=도장·서명 합성됨 / 또는 원본) → 페이지별 캔버스 렌더 + 스캔효과 + merge-images → 스캔본 PDF Blob
// pdf.js는 PdfEditor와 동일하게 window.pdfjsLib(전역 스크립트)로 로드됨
// ★ 전체 스캔본/전체 다운로드: 폴더선택(showDirectoryPicker) 지원 시 선택 폴더에 저장, 미지원/차단/취소 시 개별 다운로드 폴백 (바탕화면·다운로드 '자체'는 Chrome이 거부 → 하위 폴더 선택)

export function applyScanEffect(cvs) {
  const ctx = cvs.getContext("2d"), w = cvs.width, h = cvs.height;
  const tmp = document.createElement("canvas"); tmp.width = w; tmp.height = h;
  tmp.getContext("2d").drawImage(cvs, 0, 0);
  const rnd = (a, b) => a + Math.random() * (b - a);
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  // 페이지마다 노출·기울기·종이톤을 다르게 (← '페이지마다 다른'의 핵심)
  const angle = rnd(-1.2, 1.2) * Math.PI / 180;
  const exposure = rnd(0.93, 1.06).toFixed(3);
  const contrast = rnd(0.98, 1.11).toFixed(3);
  const bgR = rnd(247, 255) | 0, bgG = rnd(244, 253) | 0, bgB = rnd(239, 250) | 0;
  const blurPx = Math.random() < 0.6 ? rnd(0.15, 0.45) : 0;
  const dx = rnd(-2, 2), dy = rnd(-2, 2);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "rgb(" + bgR + "," + bgG + "," + bgB + ")";
  ctx.fillRect(0, 0, w, h);
  ctx.save();
  ctx.translate(w / 2 + dx, h / 2 + dy);
  ctx.rotate(angle);
  ctx.filter = "contrast(" + contrast + ") brightness(" + exposure + ")" + (blurPx ? " blur(" + blurPx.toFixed(2) + "px)" : "");
  ctx.drawImage(tmp, -w / 2, -h / 2);
  ctx.restore(); ctx.filter = "none";
  // 불균일 음영(얼룩) — 페이지마다 위치·개수·농도 다름 (일부는 화면 밖→부분 그림자)
  const shN = 3 + (Math.random() * 4 | 0);
  for (let s = 0; s < shN; s++) {
    const sx = rnd(-0.1, 1.1) * w, sy = rnd(-0.1, 1.1) * h;
    const sr = rnd(0.12, 0.4) * Math.max(w, h);
    const sa = rnd(0.008, 0.026);
    const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
    g.addColorStop(0, "rgba(0,0,0," + sa.toFixed(3) + ")");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  }
  // 가장자리 비네팅(페이지마다 세기 다름)
  const vig = rnd(0.015, 0.05);
  const vg = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.5, w / 2, h / 2, Math.max(w, h) * 0.78);
  vg.addColorStop(0, "rgba(0,0,0,0)");
  vg.addColorStop(1, "rgba(0,0,0," + vig.toFixed(3) + ")");
  ctx.fillStyle = vg; ctx.fillRect(0, 0, w, h);
  // 입자감 — 균일 그레인 대신 페이지마다 강도 확 다르게(0 포함 → 덜 인위적)
  const noiseAmt = pick([0, 0, 1.5, 2.5, 4]);
  if (noiseAmt > 0) {
    const id = ctx.getImageData(0, 0, w, h), d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const nz = (Math.random() * 2 - 1) * noiseAmt;
      d[i] += nz; d[i + 1] += nz; d[i + 2] += nz;
    }
    ctx.putImageData(id, 0, 0);
  }
  tmp.width = 0; tmp.height = 0;
}

// 단일 PDF Blob → 스캔효과 입힌 PDF Blob
export async function makeScannedPdf(pdfBlob) {
  const url = URL.createObjectURL(pdfBlob);
  try {
    const pdfDoc = await window.pdfjsLib.getDocument(url).promise;
    const blobs = [];
    for (let i = 0; i < pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i + 1);
      const base1 = page.getViewport({ scale: 1 });
      let rscale = Math.max(1.7, Math.min(1.9, 1600 / base1.width));
      let blob = null;
      for (let attempt = 0; attempt < 5 && !blob; attempt++) {
        const vp = page.getViewport({ scale: rscale });
        const cvs = document.createElement("canvas");
        cvs.width = vp.width; cvs.height = vp.height;
        const ctx = cvs.getContext("2d");
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
        applyScanEffect(cvs);
        blob = await new Promise(function (resolve) {
          cvs.toBlob(function (b) { resolve(b); }, "image/jpeg", 0.82);
        });
        cvs.width = 0; cvs.height = 0;
        if (!blob) rscale = Math.max(1.8, rscale * 0.72);
      }
      if (!blob) throw new Error("이미지 변환 실패 (페이지가 너무 큽니다)");
      blobs.push(blob);
    }
    const fd = new FormData();
    for (let k = 0; k < blobs.length; k++) {
      fd.append("pages", blobs[k], "page_" + k + ".jpg");
    }
    const token = localStorage.getItem("token") || "";
    const res = await fetch("/api/pdf-editor/merge-images", {
      method: "POST",
      headers: token ? { "Authorization": "Bearer " + token } : {},
      body: fd,
    });
    if (!res.ok) throw new Error("스캔본 생성 실패");
    return await res.blob();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// 단일 Blob 즉시 다운로드
function downloadBlob(blob, fname) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fname;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
}

// 폴더 저장 시 동일 이름 덮어쓰기 방지
function uniqScanName(name, used) {
  let nm = name; let i = 1;
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  while (used.has(nm)) { nm = base + " (" + (i++) + ")" + ext; }
  used.add(nm); return nm;
}

// 전체 스캔본: 폴더선택(showDirectoryPicker) 지원 시 고른 폴더에 저장, 미지원/차단 시 개별 다운로드(폴백), 취소 시 중단
//  서명(날인)본 → produce가 스캔효과 PDF 반환 + 파일명 _스캔본 / 미서명 → 원본 그대로. 실패는 건너뜀
//  targets: {key,name,signed?,getBlob?}[], produce: (t)=>Promise<Blob> → {ok, fail, cancelled}
//  getDir(선택): 조합 저장 폴더(projectFolder.projectDir) — 주면 폴더를 다시 묻지 않고 그 폴더에(같은 이름 덮어씀 · 2026-11-01)
export async function scanAndSaveToFolder(targets, produce, _toast, getDir) {
  const nameOf = (t, i) => t.signed
    ? String(t.name || ("문서" + (i + 1))).replace(/\.[^.]+$/i, "") + "_스캔본.pdf"
    : String(t.name || ("문서" + (i + 1)));
  const picker = window.showDirectoryPicker;
  if (typeof picker === "function") {
    let dir = null;
    try {
      dir = getDir ? await getDir() : await chooseFolderOnce();   // 안내 창을 거쳐 고르기(문서 · 바탕화면 자체를 골라 막히던 것 · 2026-11-02)
    } catch (e) {
      if (e && (e.name === "AbortError" || e.name === "SaveCancelled")) return { ok: 0, fail: 0, cancelled: true };
      dir = null;
    }
    if (dir) {
      let ok = 0, fail = 0; const used = new Set();
      for (let i = 0; i < targets.length; i++) {
        let blob = null;
        try { blob = await produce(targets[i]); } catch (e) { blob = null; }
        if (!blob) { fail++; continue; }
        try {
          const fh = await dir.getFileHandle(getDir ? nameOf(targets[i], i) : uniqScanName(nameOf(targets[i], i), used), { create: true });
          const ws = await fh.createWritable();
          await ws.write(blob); await ws.close();
          ok++;
        } catch (e) { fail++; }
      }
      return { ok, fail, cancelled: false };
    }
  }
  let ok = 0, fail = 0;
  for (let i = 0; i < targets.length; i++) {
    let blob = null;
    try { blob = await produce(targets[i]); } catch (e) { blob = null; }
    if (!blob) { fail++; continue; }
    downloadBlob(blob, nameOf(targets[i], i));
    await new Promise(function (r) { setTimeout(r, 250); });
    ok++;
  }
  return { ok, fail, cancelled: false };
}
